/**
 * SignInAlerts — what the sign-in form says above its fields (module-private to `<LoginForm>`).
 *
 * LOGIN-CONVERGENCE (2026-10-11), one place for every sentence the form shows about an attempt:
 *   - the form's own word on the last attempt (`form.refusal`, W4 + W7): a sentence the surface
 *     already translated, or a `login`-namespace leaf translated here — so a refusal reads in the
 *     reader's language, never in the server's English. A LOCKED refusal (W8) is the locked banner:
 *     the warning tone, because only something outside the form can lift it;
 *   - otherwise the auth context's own `error` (a failure nothing named);
 *   - the info message, and the notice that a password reset has finished (W6).
 *
 * The throttle's notice is NOT here: `<LoginForm>` renders it beside the submit it disables, the one
 * file `rateLimitRefusalsAreVisible` reads for both halves (Q-UMB-21).
 *
 * @module @puffin-core/login-shell/components/loginForm/SignInAlerts
 */

import PropTypes from 'prop-types';
// The NARROW subpath, never the barrel: login-shell is router-free. Every line here is the ONE feedback
// component (SAVE-FEEDBACK-001 §1; SAVE-FEEDBACK-CANON-001 P3), never hand-written `.alert` markup.
import { InlineFeedback } from '@puffin-core/page-shell/inline-feedback';

/**
 * The attempt alert's id: the credential fields name it in `aria-describedby` (W11), so a screen
 * reader reads the refusal with the field it is about. One sign-in form per page.
 */
export const SIGN_IN_ALERT_ID = 'login-error';

/** The throttle notice's wrapper id (W13): a locked form's fields name it too. */
export const SIGN_IN_RATE_LIMIT_ID = 'login-rate-limit';

/**
 * Whether the form is showing a word about the last attempt.
 *
 * @param {Object} form - `useLoginForm`'s state.
 * @returns {boolean}
 */
export function hasAttemptAlert(form) {
  return Boolean(form.refusal || form.error);
}

/**
 * The ids that describe the credential fields right now (W11 + W13): the attempt alert, then the
 * throttle notice while the form is locked. Empty when the form says nothing.
 *
 * @param {Object} form - `useLoginForm`'s state.
 * @returns {string[]}
 */
export function describingAlertIds(form) {
  const ids = [];
  if (hasAttemptAlert(form)) ids.push(SIGN_IN_ALERT_ID);
  if (form.rateLimit?.isLimited) ids.push(SIGN_IN_RATE_LIMIT_ID);
  return ids;
}

/**
 * @param {Object} props
 * @param {Object} props.form - `useLoginForm`'s state.
 * @param {(key: string) => string} props.translate - The `login`-namespace translator.
 * @returns {React.JSX.Element}
 */
export function SignInAlerts({ form, translate }) {
  const { refusal } = form;
  const refusalText = refusal ? refusal.message || translate(refusal.messageKey) : null;

  let attemptAlert = null;
  if (refusal && refusal.locked) {
    attemptAlert = (
      <InlineFeedback
        tone="warning"
        id={SIGN_IN_ALERT_ID}
        message={refusalText}
        className="mb-4"
        testId="login-locked"
      />
    );
  } else if (refusalText || form.error) {
    attemptAlert = (
      <InlineFeedback
        tone="error"
        id={SIGN_IN_ALERT_ID}
        message={refusalText || form.error}
        className="mb-4"
        testId="login-error"
      />
    );
  }

  return (
    <>
      {attemptAlert}
      {form.infoMessage && (
        <InlineFeedback
          tone="info"
          message={form.infoMessage}
          className="mb-4"
          testId="login-info"
        />
      )}
      {form.passwordResetDone && (
        <InlineFeedback
          tone="success"
          message={translate('login.passwordReset.done')}
          className="mb-4"
          testId="login-reset-done"
        />
      )}
    </>
  );
}

SignInAlerts.propTypes = {
  form: PropTypes.shape({
    refusal: PropTypes.shape({
      message: PropTypes.string,
      messageKey: PropTypes.string,
      locked: PropTypes.bool,
    }),
    error: PropTypes.string,
    infoMessage: PropTypes.string,
    passwordResetDone: PropTypes.bool,
  }).isRequired,
  translate: PropTypes.func.isRequired,
};

export default SignInAlerts;
